import BodyText from "components/Typography/BodyText"
import { TabsTriggerProps } from "components/EvtKit/tabs/TabsTrigger"
import { TabsContext } from "components/EvtKit/tabs/Tabs"

import clsx from "clsx"
import { memo, useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"

const TabsTriggerContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TabsTriggerBg = styled.div`
  height: 40px;
  width: 250px;
  border-radius: 10px;
  position: absolute;
  z-index: 1;
  transform-origin: center left;

  &.isActive {
    background: linear-gradient(
      251.11deg,
      ${(props) => props.theme.palette.primary_alt_dark} 29.56%,
      ${(props) => props.theme.palette.primary_dark} 77.48%
    );
  }

  &.md {
    width: 342px;
  }

  &.right {
    animation: slideToRight 0.2s ease-out;
  }

  &.left {
    animation: slideToLeft 0.2s ease-out;
  }

  @keyframes slideToRight {
    0% {
      transform: translateX(-100%);
    }

    90% {
      transform: translateX(3%);
    }

    100% {
      transform: translateX(0%);
    }
  }

  @keyframes slideToLeft {
    from {
      transform: translateX(100%);
    }

    90% {
      transform: translateX(-3%);
    }

    to {
      transform: translateX(0%);
    }
  }
`

const TabsTriggerButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  z-index: 2;
`

type AnimationDirection = "none" | "left" | "right"

const TabsTriggerGradient = (props: TabsTriggerProps) => {
  const { label, triggerId, bgClassName } = props

  const { onActiveTabIdChange, defaultActiveTabId, activeTabId, tabsIdList } = useContext(TabsContext)

  const [slide, setSlide] = useState<AnimationDirection>("none")

  const handleSlide = useCallback(() => {
    const id = (activeTabId || defaultActiveTabId) ?? ""

    const indexOfActiveTab = tabsIdList.indexOf(id)
    const indexOfTriggerId = tabsIdList.indexOf(triggerId)

    let direction: AnimationDirection = "none"

    if (indexOfTriggerId > indexOfActiveTab) {
      direction = "right"
    }

    if (indexOfTriggerId < indexOfActiveTab) {
      direction = "left"
    }

    setSlide(direction)
  }, [activeTabId, defaultActiveTabId, tabsIdList, triggerId])

  const handleTrigger = useCallback(() => {
    handleSlide()

    onActiveTabIdChange(triggerId)
  }, [onActiveTabIdChange, triggerId, handleSlide])

  useEffect(() => {
    if (!activeTabId && defaultActiveTabId) {
      onActiveTabIdChange(defaultActiveTabId)
    }
  }, [])

  const handleAnimationEnd = useCallback(() => {
    setSlide("none")
  }, [])

  const isActive = activeTabId === triggerId

  const labelColor = isActive ? "white" : "primary-dark"

  return (
    <TabsTriggerContainer>
      <TabsTriggerBg
        className={clsx(
          {
            isActive,
            left: slide === "left",
            right: slide === "right"
          },
          bgClassName
        )}
        onAnimationEnd={handleAnimationEnd}
      />
      <TabsTriggerButton onClick={handleTrigger}>
        <BodyText size="small" weight="semi-bold" color={labelColor}>
          {label}
        </BodyText>
      </TabsTriggerButton>
    </TabsTriggerContainer>
  )
}

export default memo(TabsTriggerGradient)
